import React, { lazy, Suspense, useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { sectionOrder } from './data/conclaveData';
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import { ModalProvider, useModal } from './context/ModalContext';
import SponsorshipModal from './components/forms/SponsorshipModal';
import DelegateEnquiryModal from './components/forms/DelegateEnquiryModal';
import useHeadlessDetection from './utils/useHeadlessDetection'; // 👈 Added this line

// Lazy-loaded sections
const componentMap = {
  ConclaveOverviewSection: lazy(() => import('./components/ConclaveOverviewSection')),
  AdvisoryBoardSection: lazy(() => import('./components/AdvisoryBoardSection')),
  AboutSection: lazy(() => import('./components/AboutSection')),
  VisionObjectivesSection: lazy(() => import('./components/VisionObjectivesSection')),
  TechnologyDeepDivesSection: lazy(() => import('./components/TechnologyDeepDivesSection')),
  IndustryVerticalTracksSection: lazy(() => import('./components/IndustryVerticalTracksSection')),
  ParticipantProfileSection: lazy(() => import('./components/ParticipantProfileSection')),
 // AgendaSection: lazy(() => import('./components/AgendaSection')),
  ScheduleSection: lazy(() => import('./components/ScheduleSection')),
  StartupShowcaseSection: lazy(() => import('./components/StartupShowcaseSection')),
  SpeakersSection: lazy(() => import('./components/SpeakersSection')),
  GetInvolvedSection: lazy(() => import('./components/GetInvolvedSection')),
  SponsorsSection: lazy(() => import('./components/SponsorsSection')),
  ExhibitorsSection: lazy(() => import('./components/ExhibitorsSection')),
  PartnersSection: lazy(() => import('./components/PartnersSection')),
  VenueSection: lazy(() => import('./components/VenueSection')),
  ContactSection: lazy(() => import('./components/ContactSection')),
  AudienceSection: lazy(() => import('./components/AudienceSection')),
  FocusAreas: lazy(() => import('./components/FocusAreasSection')),
  KeyVerticals: lazy(() => import('./components/KeyVerticalsSection')),
  AudienceMetricsBand: lazy(() => import('./components/AudienceMetricsBandSection')),
  PartnerExplorer: lazy(() => import('./components/PartnerExplorer')),
  Cohosting: lazy(() => import('./components/cohostingpartner')),
  //PartnersPage: lazy(() => import('./components/PartnersPage')),
};

// Spinner loader
const SectionLoader = () => (
  <div className="section-padding">
    <div className="container-custom">
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    </div>
  </div>
);

function AppContent() {
  const [involvedIndex, setInvolvedIndex] = useState(null);
  const [delayedSections, setDelayedSections] = useState([]);

  // ✅ Use headless detection hook
  useHeadlessDetection();

  // 🧱 Delay sensitive sections after load
  useEffect(() => {
    const delayMap = {
      SpeakersSection: 1000,
      ScheduleSection: 2000,
      SponsorsSection: 3000,
      StartupShowcaseSection: 2000,
      AboutSection: 1500,
    };

    Object.entries(delayMap).forEach(([sectionName, delay]) => {
      setTimeout(() => {
        setDelayedSections((prev) => [...prev, sectionName]);
      }, delay);
    });
  }, []);

  // 🔒 Disable right-click, drag, selection, and Ctrl+P
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'production' 
    ) {
      const disableContextMenu = (e) => e.preventDefault();
      const disableDragStart = (e) => e.preventDefault();
      const disableSelectStart = (e) => e.preventDefault();
      const preventPrint = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
          e.preventDefault();
          alert('Printing is disabled on this site.');
        }
      };

      document.addEventListener('contextmenu', disableContextMenu);
      document.addEventListener('dragstart', disableDragStart);
      document.addEventListener('selectstart', disableSelectStart);
      document.addEventListener('keydown', preventPrint);

      return () => {
        document.removeEventListener('contextmenu', disableContextMenu);
        document.removeEventListener('dragstart', disableDragStart);
        document.removeEventListener('selectstart', disableSelectStart);
        document.removeEventListener('keydown', preventPrint);
      };
    }
  }, []);

  const visibleSections = sectionOrder
    .filter((section) => section.display && section.name !== 'Footer')
    .sort((a, b) => a.order - b.order);

  const footerSection = sectionOrder.find((section) => section.name === 'Footer');
  const showFooter = footerSection?.display;

  return (
    <div className="App bg-darkbg text-white min-h-screen">
      <Header setInvolvedIndex={setInvolvedIndex} />

      <main>
        <Hero setInvolvedIndex={setInvolvedIndex} />

        <Suspense fallback={<SectionLoader />}>
          {visibleSections.map((section) => {
            const Component = componentMap[section.name];
            if (!Component) return null;

            const delayedList = [
              'SpeakersSection',
              'AgendaSection',
              'SponsorsSection',
              'StartupShowcaseSection',
              'AboutSection',
            ];
            const isDelayed = delayedList.includes(section.name);

            if (isDelayed && !delayedSections.includes(section.name)) {
              return (
                <section key={section.name} className="w-full">
                  <div className="max-w-[1728px] mx-auto w-full px-4">
                    <SectionLoader />
                  </div>
                </section>
              );
            }

            return (
              <section key={section.name} className="w-full">
                <div className="max-w-[1728px] mx-auto w-full px-4">
                  {section.name === 'GetInvolvedSection' ? (
                    <Component
                      sectionConfig={section}
                      involvedIndex={involvedIndex}
                      resetInvolvedIndex={() => setInvolvedIndex(0)}
                    />
                  ) : (
                    <Component sectionConfig={section} />
                  )}
                </div>
              </section>
            );
          })}
        </Suspense>

        {showFooter && (
          <section className="w-full">
            <div className="max-w-[1728px] mx-auto w-full px-4">
              <Footer />
            </div>
          </section>
        )}
      </main>

      <GlobalModalRenderer />

      <ToastContainer
        position="top-center"
        style={{ top: '50%' }}
        autoClose={3000}
        theme="dark"
      />
    </div>
  );
}

// Modal rendering logic
function GlobalModalRenderer() {
  const { activeModal, closeModal, modalProps } = useModal();

  return (
    <>
      {activeModal === 'sponsorship' && (
        <SponsorshipModal isOpen onClose={closeModal} {...modalProps} />
      )}
      {activeModal === 'delegates' && (
        <DelegateEnquiryModal isOpen onClose={closeModal} {...modalProps} />
      )}
    </>
  );
}

// App wrapper with ModalContext
function App() {
  return (
    <ModalProvider>
      <AppContent />
    </ModalProvider>
  );
}

export default App;
